import React, { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import { T } from '@transifex/react';
import { CheckCircle } from 'src/ui-new/icons';
import { createPrefixer } from '../../utils';

const p = createPrefixer('wh-order-complete-');

const classes = {
  wrapper: p`wrapper`,
  icon: p`icon`,
  title: p`title`,
  subtext: p`subtext`,
  buttons: p`buttons`,
  informationContainer: p`information-container`,
  mainContent: p`main-content`,
};

export type OrderCompleteProps = {
  orderCompleteTitle: string | ReactNode;
  orderCompleteSubtext: string | ReactNode;
  primaryButton?: ReactNode;
  secondaryButtons?: ReactNode[];
};

const iconPop = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to   { opacity: 1; transform: scale(1); }
`;
const riseFade = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
`;

const OrderCompleteDiv = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  height: 100%;
  width: 100%;

  /* One-shot celebratory entrance for the terminal success screen: the
     checkmark pops in (fade + slight grow from 0.9), then the title and
     subtext rise-fade a beat later in sequence. Gated on
     prefers-reduced-motion so reduced-motion users see everything at once,
     fully visible; the both fill preserves the hidden initial state until
     each delay elapses. */
  @media (prefers-reduced-motion: no-preference) {
    .${classes.icon} {
      animation: ${iconPop} 320ms var(--wh-ease-out) both;
    }
    .${classes.title} {
      animation: ${riseFade} 300ms var(--wh-ease-out) both;
      animation-delay: 120ms;
    }
    .${classes.subtext} {
      animation: ${riseFade} 300ms var(--wh-ease-out) both;
      animation-delay: 200ms;
    }
  }

  .${classes.mainContent} {
    height: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: end;
  }

  .${classes.icon} {
    font-size: 4.375rem;
    color: ${(props) => props.theme.colors.success100};
  }

  .${classes.title} {
    font-size: 1.563rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    text-wrap: balance;
  }

  .${classes.subtext} {
    font-size: 0.833rem;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 1rem;
    text-wrap: pretty;
  }

  .ant-btn {
    height: 2.5rem;
    margin-bottom: 0.4rem;
    width: 100%;
  }

  .${classes.buttons} {
    width: 100%;
    height: 50%;
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  @media screen and (min-width: 992px) {
    display: flex;
    flex-direction: column;
    text-align: center;
    height: 100%;
    width: 100%;

    .${classes.mainContent} {
      height: 25%;
    }

    .${classes.icon} {
      margin-right: 0.5rem;
    }

    .${classes.title} {
    }

    .${classes.subtext} {
    }

    .ant-btn {
      width: fit-content;
      margin: 1rem 0.5rem 0 0;
    }

    .${classes.buttons} {
      width: 100%;
      height: 75%;
      display: flex;
      flex-direction: row;
      justify-content: center;
    }
  }
`;

export const OrderCompleteView: React.FC<OrderCompleteProps> = ({
  orderCompleteSubtext,
  orderCompleteTitle,
  primaryButton,
  secondaryButtons,
}) => {
  return (
    <OrderCompleteDiv className={classes.wrapper}>
      <div className={classes.mainContent}>
        <div className={classes.icon}>
          <CheckCircle size="1em" />
        </div>
        <div className={classes.title}>
          {typeof orderCompleteTitle === 'string' ? (
            <T _str="{title}" title={orderCompleteTitle} />
          ) : (
            <>{orderCompleteTitle}</>
          )}
        </div>
        <div className={classes.subtext}>
          {typeof orderCompleteSubtext === 'string' ? (
            <T _str="{subtext}" subtext={orderCompleteSubtext} />
          ) : (
            <>{orderCompleteSubtext}</>
          )}
        </div>
      </div>
      <div className={classes.buttons}>
        {primaryButton}
        {secondaryButtons?.map((button) => (
          <>{button}</>
        ))}
      </div>
    </OrderCompleteDiv>
  );
};
